import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { formatDateTime } from "@/lib/format";

interface ApiToken {
  id: number;
  description?: string | null;
  created_at: string;
  created_by?: string | null;
  is_active: boolean;
  last_used_at?: string | null;
}

export function ApiTokensSection() {
  const [items, setItems] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingDescription, setPendingDescription] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ApiToken[]>("/admin/api-tokens");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konnte Tokens nicht laden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function onStart(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const desc = pendingDescription.trim();
    if (!desc) return;
    setEmail("");
    setSendError(null);
    setEmailDialogOpen(true);
  }

  async function onSendMail() {
    const desc = pendingDescription.trim();
    const target = email.trim();
    if (!desc) return;
    if (!target) {
      setSendError("Bitte eine E-Mail-Adresse eingeben.");
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await api.post("/admin/api-tokens/email", { email: target, description: desc });
      setEmailDialogOpen(false);
      setPendingDescription("");
      setEmail("");
      await reload();
    } catch (err) {
      setSendError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Versand fehlgeschlagen.",
      );
    } finally {
      setSending(false);
    }
  }

  async function deactivate(token: ApiToken) {
    if (!window.confirm(`Token #${token.id} wirklich deaktivieren?`)) return;
    try {
      await api.post(`/admin/api-tokens/${token.id}/deactivate`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Token konnte nicht deaktiviert werden.");
    }
  }

  return (
    <Card
      title="HTTP-API Tokens"
      description="Tokens für die externe HTTP-API (eine Doku-Mail und eine Token-Mail werden automatisch verschickt)."
      actions={
        <form onSubmit={onStart} className="flex flex-wrap gap-2">
          <input
            className="eh-input"
            placeholder="Beschreibung"
            value={pendingDescription}
            onChange={(e) => setPendingDescription(e.target.value)}
            required
            style={{ width: "240px" }}
          />
          <Button type="submit">Token erzeugen</Button>
        </form>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> <span>Lädt …</span>
        </div>
      )}
      {!loading && error && (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      )}
      {!loading && !error && (
        <Table<ApiToken>
          rowKey={(row) => row.id}
          rows={items}
          stickyEndColumnCount={1}
          emptyText="Noch keine Tokens vorhanden."
          columns={[
            {
              key: "id",
              header: "ID",
              sortable: true,
              sortValue: (r) => r.id,
              render: (r) => r.id,
              className: "w-12",
            },
            {
              key: "description",
              header: "Beschreibung",
              sortable: true,
              sortValue: (r) => r.description || "",
              render: (r) => r.description || "—",
            },
            {
              key: "is_active",
              header: "Status",
              sortable: true,
              sortValue: (r) => r.is_active,
              render: (r) =>
                r.is_active ? (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                    aktiv
                  </span>
                ) : (
                  <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    inaktiv
                  </span>
                ),
            },
            {
              key: "created_at",
              header: "Erstellt",
              sortable: true,
              sortValue: (r) => r.created_at,
              render: (r) => formatDateTime(r.created_at),
            },
            {
              key: "created_by",
              header: "Erstellt von",
              sortable: true,
              sortValue: (r) => r.created_by || "",
              render: (r) => r.created_by || "—",
            },
            {
              key: "last_used_at",
              header: "Zuletzt benutzt",
              sortable: true,
              sortValue: (r) => r.last_used_at,
              render: (r) => formatDateTime(r.last_used_at),
            },
            {
              key: "actions",
              header: "",
              headerClassName: "min-w-[9rem]",
              className: "min-w-[9rem]",
              render: (r) =>
                r.is_active ? (
                  <Button size="sm" variant="danger" onClick={() => deactivate(r)}>
                    Deaktivieren
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        dirty={email.trim() !== ""}
        title="Token per E-Mail versenden"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEmailDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={onSendMail} loading={sending}>
              Senden
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextInput label="Beschreibung" value={pendingDescription} readOnly />
          <TextInput
            label="Empfänger-E-Mail"
            type="email"
            placeholder="name@firma.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Es werden zwei getrennte E-Mails verschickt: zuerst die ausführliche Dokumentation
            zur HTTP-API, anschließend eine zweite E-Mail mit dem Token selbst.
          </p>
          {sendError && <Alert variant="error">{sendError}</Alert>}
        </div>
      </Dialog>
    </Card>
  );
}
